import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FileParams, theme } from '@zonofi/common';
import { ActivityIndicator } from 'react-native';
import { ReactComponent as DownloadIcon } from '@zonofi/svg-icons/download.svg';
import { MediaAction, useHandleMediaDownload } from './handle-media-download';
import { dispatch, ErrorVariants, showToast } from '@zonofi/zono-money-store';
import { downloadFile } from '../../helper/download-file';

const MAX_RETRIES = 3;

export const ImageView: React.FC<FileParams> = (props) => {
  const { messageId, filename = '', caption, generateLinkParams, link } = props;

  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [hasFailed, setHasFailed] = useState(false);
  const retryRef = useRef(0);
  const forceRef = useRef(false);

  const onError = useCallback(() => {
    dispatch(
      showToast({
        canDisplay: true,
        message: 'Error downloading media',
        responseStatus: 'Error',
        type: ErrorVariants.DANGER,
      })
    );
  }, []);

  const onSuccess = useCallback(
    async (url: string, action: MediaAction) => {
      try {
        switch (action) {
          case 'open':
            window.open(url, '_blank');
            break;

          case 'download':
            downloadFile({ url, fileName: filename });
            break;

          case 'render':
            setImageSrc(url);
            break;

          default:
            console.warn(`Unhandled media action: ${action}`);
        }
      } catch (err) {
        console.error(`Error in onSuccess (${action}):`, err);
        onError();
      }
    },
    [downloadFile, filename, onError]
  );

  const { getMedia, isLoading } = useHandleMediaDownload({
    onSuccess,
    onError,
    filename,
    link,
  });

  const callHandleMedia = async (action: MediaAction, force = false) => {
    if (action === 'render' && force) {
      forceRef.current = true;
    }

    await getMedia(generateLinkParams, action, force || forceRef.current);
  };

  // ✅ Load cached image or trigger render
  useEffect(() => {
    callHandleMedia('render');
  }, [messageId]);

  const openImage = async () => {
    await callHandleMedia('open');
  };

  const downloadImage = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    await callHandleMedia('download');
  };

  const handleImageError = async () => {
    console.warn(
      'Image failed to load — retrying via media handler (forced S3)'
    );
    if (retryRef.current < MAX_RETRIES) {
      retryRef.current += 1;
      await callHandleMedia('render', true);
    } else {
      setHasFailed(true);
    }
  };

  const isCurrentlyLoading = (!imageSrc && !hasFailed) || isLoading;

  return (
    <div className="image-wrapper">
      {isCurrentlyLoading ? (
        <ActivityIndicator size={20} color={theme.text.secondary} />
      ) : hasFailed ? (
        <div className="font-regular image-error">Failed to load image</div>
      ) : (
        <img
          className="template-image"
          src={imageSrc!}
          alt={filename}
          onError={handleImageError}
          onClick={openImage}
        />
      )}

      {caption && <div className="image-caption">{caption}</div>}

      {generateLinkParams && (
        <button
          className="download-button"
          onClick={downloadImage}
          disabled={isLoading}
        >
          <DownloadIcon color="#363636" />
        </button>
      )}
    </div>
  );
};
